import Flex from 'frontend/commons/Flex';
import LoadSpinner from 'frontend/commons/spinner';
import { QUERY_KEYS } from 'frontend/constants';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiShare } from 'react-icons/hi';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getBanner } from '../../api/services/marcaBlancaClientServices';
import Tag from '../../commons/tag';
import ConsultaModalBanner from './components/ConsultaModalBanner';
import ImageGallery from './components/ImageGallery';
import './quill.core.css';
import './styles.css';
import { WhatsappShareButton, WhatsappIcon } from 'react-share';

const propertiesToTab = ['itinerario', 'incluye', 'hoteles', 'tarifas', 'salida', 'detalle'];

const BannerDetail = ({
  match: {
    params: { id },
  },
}) => {
  const { t } = useTranslation(['translations', 'layout']);
  const { pathname } = useLocation();
  const token = useSelector((state) => state.applicationState.token);
  const web = useSelector((state) => state.applicationState.web);
  const url = web === 'localhost' ? 'http://localhost:3000' : `https://${web}`;
  const urlComplete = `${url}${pathname}`;

  const translationMap = {
    itinerario: t('layout:packages.details.itinerary'),
    incluye: t('layout:packages.details.included'),
    hoteles: t('layout:packages.details.hotels'),
    tarifas: t('layout:packages.details.rates'),
    salida: t('layout:packages.details.departure'),
    detalle: t('layout:packages.details.detail'),
  };

  const translationMapInverse = {
    itinerary: 'itinerario',
    includes: 'incluye',
    hotels: 'hoteles',
    rates: 'tarifas',
    departure: 'salida',
    detail: 'detalle',
  };

  const orderSpanishArray = (englishArray, spanishArray) => {
    const orderedSpanishArray = englishArray
      .map((englishKey) => translationMapInverse[englishKey.toLowerCase()])
      .filter((key) => spanishArray.includes(key));

    return orderedSpanishArray;
  };

  const { data: bannerData, isLoading } = useQuery(
    [QUERY_KEYS.BANNER, id],
    () => getBanner(id, token),
    {
      enabled: !!id,
      staleTime: Infinity,
    },
  );
  const [consultaBanner, setConsultaBanner] = useState();
  const [currentTab, setCurrentTab] = useState();
  const [shareUrl, setShareUrl] = useState('');
  const encode = encodeURIComponent;

  useEffect(() => {
    if (bannerData) {
      setShareUrl(`${url}/linkshare/${encode(bannerData.id)}`);
    }
  }, [bannerData]);

  const shareBanner = async () => {
    navigator.clipboard.writeText(shareUrl);
    alert(t('common.link_copied'));
  };

  const tabs = useMemo(() => {
    if (bannerData) {
      const arrayOrder = orderSpanishArray(bannerData.ordenSecciones, propertiesToTab);
      const tabsContent = arrayOrder
        .map((key) => {
          const content = bannerData[key];
          const translatedKey = translationMap[key.toLowerCase()] || key;
          return content ? [translatedKey, content] : undefined;
        })
        .filter(Boolean);

      return !!tabsContent?.length ? tabsContent : undefined;
    }
    return undefined;
  }, [bannerData]);

  useEffect(() => {
    if (!isLoading && bannerData && !!tabs?.length) {
      setCurrentTab(tabs[0][0]);
    }
  }, [bannerData, isLoading, tabs]);

  return (
    <>
      {bannerData && !isLoading ? (
        <Flex vertical className="banner-detail-container page-content-wrapper">
          <Flex className="banner__header">
            <h3 className="text-lg">
              <i className="fa fa-chevron-left back-button" onClick={() => history.back()} />
              {bannerData.titulo}
            </h3>
            <div className="banner__card__share">
              <span className="banner__card__share__text">{t('share')}</span>
              <Flex className="banner__card__share_section" onClick={shareBanner}>
                <HiShare className="banner__card__share__icon" />
              </Flex>
              <WhatsappShareButton url={shareUrl}>
                <Flex className="banner__card__share_section">
                  <WhatsappIcon size={32} round />
                </Flex>
              </WhatsappShareButton>
            </div>
          </Flex>
          <Flex className="boxes-wrapper">
            <Flex vertical className="boxes-wrapper banner-information">
              <ImageGallery
                items={
                  !!bannerData.bannerImagenes?.length
                    ? bannerData.imageDescriptionBanner
                      ? [bannerData.imagen, ...bannerData.bannerImagenes]
                      : [...bannerData.bannerImagenes]
                    : bannerData.imageDescriptionBanner
                      ? [bannerData.imagen]
                      : []
                }
              />
            </Flex>
            <Flex vertical className="boxes-wrapper banner-details">
              <Flex vertical className="banner-details-card card-box">
                <Flex vertical className="banner-details__payment-card__header">
                  <Flex vertical className="banner-details__payment-card__header__data">
                    <p className="banner-details__payment-card__header__title">
                      {bannerData.titulo}
                    </p>
                    <p className="banner-details__payment-card__header__description">
                      {bannerData.descripcion}
                    </p>
                  </Flex>
                  <Flex className="banner-details__tags">
                    {[bannerData.extra, bannerData.precio, bannerData.descuento]
                      .filter(Boolean)
                      .map((item, index) => (
                        <Tag key={item + index} text={item} />
                      ))}
                  </Flex>
                </Flex>
                <Flex vertical className="banner-details__payment-card__footer">
                  <Flex vertical className="banner-details__payment-card__footer__info"></Flex>
                  <button
                    className="banner-details-button banner-details__ask-button"
                    onClick={() => {
                      setConsultaBanner({ ...bannerData, link: urlComplete });
                    }}
                  >
                    {t('ask_about')}
                  </button>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
          {!!currentTab && !!tabs?.length && (
            <Flex vertical className="card-box banner-information__description-card">
              <ul className="tabs-menu with-bottom-divider">
                {tabs.map(([tabName]) => (
                  <li
                    key={tabName}
                    className={`tabs-menu__tab${tabName === currentTab ? ' active' : ''}`}
                    onClick={() => setCurrentTab(tabName)}
                  >
                    {tabName}
                  </li>
                ))}
              </ul>
              <Flex
                dangerouslySetInnerHTML={{
                  __html: Object.fromEntries(tabs)[currentTab],
                }}
                vertical
                className="banner-information__description-card__inserted-html ql-editor"
                id="tabContent"
              />
            </Flex>
          )}
          <ConsultaModalBanner
            consultaBanner={consultaBanner}
            onClose={(e) => setConsultaBanner(undefined)}
          />
        </Flex>
      ) : (
        <Flex
          style={{
            flex: 1,
            height: '30rem',
            justifyContent: 'center',
            padding: '8rem 0',
          }}
        >
          <LoadSpinner />
        </Flex>
      )}
    </>
  );
};

export default BannerDetail;
